import { getAppConfigSingleton } from "@expert/app-config";
import { useToast } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { useCallback, useState } from "react";
import { shallow } from "zustand/shallow";
import { useVerifiedCustomerStore } from "@/customer-info";
import { usePartner, useSession } from "@/sdk";
import { getHashedValue, removeCountryCode } from "@/shared-utils";
import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { validateSummary } from "../validateSummary";
import { useSessionSummaryDispatchActions } from "./hooks";
import { defaultAdditionalSummaryTimes } from "./utils";

const logger = getLogger({ module: "sessionSummary" });

/**
 * Hook for session summary logic to separate from UI components
 */
export const useSummary = () => {
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();
    const partner = usePartner();
    const toast = useToast();

    const [summaryGenerated, setSummaryGenerated] = useState(false);

    const { summary, submitted, setSessionSummary } = useSessionSummaryStore((state) => state, shallow);
    const { dispatchSessionSummaryStats, dispatchSessionSubmit } = useSessionSummaryDispatchActions();
    const [setSubmitted, autogeneratedSummary] = useSessionSummaryStore((state) => [
        state.setSubmitted,
        state.autogeneratedSummary,
    ]);
    const { mdn, accountNumber } = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(activeSession.id).customer || {},
    );

    const validAccountNumber =
        accountNumber !== undefined &&
        (appConfig.getPartnersConfig(partner).allowedAccountNumberLengths?.includes(accountNumber.length) ?? false);

    const {
        requiresManualCopy,
        gaiaSummaryModel,
        sessionSummaryTimes = defaultAdditionalSummaryTimes,
    } = appConfig.getPartnersConfig(partner);

    const shouldSendSummary = !submitted && summary;

    const { resolutionStatus, category, subcategory } = useSessionSummaryStore();

    const setSessionSummaryFromGaia = useCallback(
        async (setSummaryLoaded: (arg0: boolean) => void, agentCallSid?: string) => {
            logger.trace({ agentCallSid, sessionId: activeSession.id, partner }, "Gaia summary fetch check");

            const generatedSummary = agentCallSid
                ? await getGaiaSummary(
                      { callSid: agentCallSid, sessionId: activeSession.id, partner },
                      gaiaSummaryModel,
                  )
                : await getBackupGaiaSummary({ sessionId: activeSession.id, partner }, gaiaSummaryModel);

            if (!generatedSummary) {
                logger.warn("Received empty summary from Gaia");
            } else {
                setSessionSummary({ value: generatedSummary, autoGenerated: true });
                setSummaryGenerated(!!generatedSummary);
            }
            setSummaryLoaded(true);
        },
        [activeSession.id, partner, setSessionSummary, gaiaSummaryModel],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    const onSessionSummaryCompleted = useCallback(
        async (
            closeModal: () => void,
            update: string,
            expirationTimestamp: number,
            shouldSendToPartnerTool?: boolean,
            agentCallSid?: string,
        ) => {
            const { isError, response } = validateSummary({ mdn, summary: update });

            if (!mdn || isError) {
                const errorMessage = `Summary NOT sent${mdn ? "" : " - MDN was not verified."}`;
                logger.error(response ?? errorMessage);
                toast.error(errorMessage);
                closeModal();
                return;
            }

            const mdnFormatted = removeCountryCode(mdn);
            const mdnHashed = await getHashedValue(mdnFormatted);

            const extraSessionSummaryPayloadProps =
                appConfig.getPartnersConfig(partner).extraSessionSummaryPayloadProps;

            const sessionSummaryPayload = {
                accountNo: accountNumber,
                remarkText: update,
                resolutionStatus,
                ...extraSessionSummaryPayloadProps,
                ...(category ? { category } : {}),
                ...(subcategory ? { subcategory } : {}),
            };

            // Summary -> EIP. If there is a valid accountNumber EIP will also send to partner tool (ACSS/Customer Central)
            dispatchSessionSubmit({ agentCallSid, mdn: mdnFormatted, mdnHashed, sessionSummaryPayload });

            dispatchSessionSummaryStats({
                agentCallSid,
                update,
                autogeneratedSummary,
                expirationTimestamp,
                summaryWasCopiedToClipboard: !shouldSendToPartnerTool,
                summaryWasSubmittedViaPartnerTool: shouldSendToPartnerTool,
                timeRanOut: false,
                gaiaSummaryModel,
            });

            setSubmitted(true);
            closeModal();
            if (shouldSendToPartnerTool) toast.success("Summary sent");
        },
        [
            mdn,
            accountNumber,
            appConfig.getPartnersConfig,
            partner,
            setSubmitted,
            toast,
            dispatchSessionSummaryStats,
            validAccountNumber,
            autogeneratedSummary,
            gaiaSummaryModel,
            dispatchSessionSubmit,
            resolutionStatus,
            category,
            subcategory,
        ],
    );

    const onDismiss = useCallback(
        async (closeModal: () => void, agentCallSid?: string, { checkSummary = true } = {}) => {
            logger.trace("Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                dispatchSessionSummaryStats({
                    agentCallSid,
                    update: summary,
                    autogeneratedSummary,
                    expirationTimestamp: 0,
                    timeRanOut: true,
                    summaryWasCopiedToClipboard: true,
                    summaryWasSubmittedViaPartnerTool: false,
                });

                toast.success("Session summary copied to clipboard");
                try {
                    await navigator.clipboard.writeText(summary);
                } catch (err) {
                    logger.warn({ err }, "Session summary not copied to clipboard");
                }
            }
        },
        [autogeneratedSummary, dispatchSessionSummaryStats, shouldSendSummary, summary, toast],
    );

    const getExtraSummaryTime = useCallback(
        (canAutosend: boolean, hasSummaryOnly: boolean) => {
            if (canAutosend) {
                return sessionSummaryTimes.autosend;
            } else if (hasSummaryOnly) {
                return sessionSummaryTimes.manualCopy;
            }
            return sessionSummaryTimes.maximum;
        },
        [sessionSummaryTimes],
    );

    return {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        setSessionSummaryFromGaia,
        onSessionSummaryCompleted,
        onDismiss,
        getExtraSummaryTime,
    };
};
